import { BannerAlertBox, BannerExpandableAlertBox } from '@entur/alert';
import dayjs from 'dayjs';
import { useOperatingMessages } from 'features/operating-messages/api/getOperatingMessages';
import { OperatingMessage } from 'features/operating-messages/components/OperatingMessage';
import { useAuth } from 'hooks/context/useAuthContext';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { OperatingMessageDto, WithRequiredId } from 'types';
import styles from './styles.module.scss';

const CLOSEDMESSAGEIDS = 'closedOperatingMessageIds';

export const OperatingMessagesBanner = () => {
  const intl = useIntl();
  const { getToken } = useAuth();

  const [closedMessageIds, setClosedMessageIds] = useState<string[]>(() => {
    const stored = localStorage.getItem(CLOSEDMESSAGEIDS);
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  });

  const { data: operatingMessagesResponse } = useOperatingMessages({
    searchParams: { filterOutdated: true },
    queryConfig: { enabled: Boolean(getToken()), staleTime: 1000 * 60 * 2, refetchInterval: 1000 * 60 * 5 },
  });

  const operatingMessages: WithRequiredId<OperatingMessageDto>[] = operatingMessagesResponse?.data?.items ?? [];

  const saveClosedMessagesToLocalStorage = (closedIds: number[]) => {
    const closedIdsAsStrings = closedIds.map(String);
    const newClosedIds = Array.from(new Set([...closedMessageIds, ...closedIdsAsStrings]));
    localStorage.setItem(CLOSEDMESSAGEIDS, JSON.stringify(newClosedIds));
    setClosedMessageIds(newClosedIds);
  };

  const openMessages = operatingMessages.filter((msg) => !closedMessageIds.includes(msg.id.toString() ?? ''));

  if (openMessages.length === 0) return null;

  if (openMessages.length === 1) {
    const message = openMessages[0];
    return (
      <BannerAlertBox
        className={styles.banner}
        variant="information"
        title={message.title}
        closable
        onClose={() => saveClosedMessagesToLocalStorage([message.id])}
      >
        <OperatingMessage fromDate={message.fromDate} toDate={message.toDate} description={message.description} />
      </BannerAlertBox>
    );
  }

  const operatingMessagesSorted = openMessages.sort((a, b) => dayjs(b.fromDate).diff(dayjs(a.fromDate)));

  return (
    <BannerExpandableAlertBox
      className={styles.banner}
      variant="information"
      title={intl.formatMessage({ id: 'menu.operatingMessages' })}
      closable
      onClose={() => saveClosedMessagesToLocalStorage(operatingMessagesSorted.map((msg) => msg.id))}
    >
      {operatingMessagesSorted.map((message) => (
        <OperatingMessage
          key={message.id}
          title={message.title}
          fromDate={message.fromDate}
          toDate={message.toDate}
          description={message.description}
        />
      ))}
    </BannerExpandableAlertBox>
  );
};
